import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NOTE_CLASS } from "../../lib/ui.js";
import { useMyPermissions } from "../../lib/permissions.js";
import { denialOrRawMessage } from "../../lib/denialReason.js";
import { listEscalationReads, openedWindow } from "../../lib/escalationClient.js";
import { displayState, formatAgo, formatWindow } from "../../lib/escalationLabels.js";
import { StateChip, TargetChip } from "./EscalationParts.jsx";

const PAGE_SIZE = 50;
const PREFETCH_PX = 160;

const fullFormatter = new Intl.DateTimeFormat(undefined, {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit", second: "2-digit",
});
const dayFormatter = new Intl.DateTimeFormat(undefined, {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
});

function grantedWindow(request) {
    if (!request.decidedAt || !request.expiresAt) return "";
    const ended = request.revokedAt && request.revokedAt < request.expiresAt
        ? request.revokedAt
        : request.expiresAt;
    return formatWindow(Math.round((ended - request.decidedAt) / 1000));
}

/**
 * The reads one escalation carried. Scroll-paginated on a keyset cursor, so a
 * read landing mid-scroll cannot shift a page boundary.
 */
function ReadTrail({ request }) {
    const caps = useMyPermissions();
    const [reads, setReads] = useState([]);
    const [cursor, setCursor] = useState("");
    const [done, setDone] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const inFlight = useRef(false);

    const load = useCallback(async (pageToken) => {
        if (inFlight.current) return;
        inFlight.current = true;
        setLoading(true);
        setError(null);
        try {
            const page = await listEscalationReads({
                requestId: request.id,
                pageSize: PAGE_SIZE,
                pageToken,
            });
            setReads((current) => (pageToken ? [...current, ...page.reads] : page.reads));
            setCursor(page.nextPageToken);
            setDone(!page.nextPageToken);
        } catch (err) {
            setError(err);
        } finally {
            inFlight.current = false;
            setLoading(false);
        }
    }, [request.id]);

    useEffect(() => {
        setReads([]);
        setCursor("");
        setDone(false);
        load("");
    }, [load]);

    const onScroll = useCallback((event) => {
        if (done || loading || !cursor) return;
        const el = event.currentTarget;
        if (el.scrollTop + el.clientHeight < el.scrollHeight - PREFETCH_PX) return;
        load(cursor);
    }, [done, loading, cursor, load]);

    return (
        <div className="grid gap-[6px] border-t border-[color:var(--line-soft)] pt-[10px]">
            <span className="text-[10px] uppercase tracking-[0.12em] text-[color:var(--muted-soft)]">
                Access log
            </span>
            <div
                onScroll={onScroll}
                aria-busy={loading ? "true" : undefined}
                className="max-h-[280px] overflow-y-auto border border-[color:var(--line)]"
            >
                {reads.length ? (
                    <>
                        {reads.map((read, index) => (
                            <div
                                key={`${read.occurredAt?.getTime() ?? index}:${index}`}
                                className="grid gap-[2px] border-b border-[color:var(--line-soft)] px-[9px] py-[6px] last:border-b-0"
                            >
                                <span className="flex flex-wrap items-baseline gap-x-[8px]">
                                    <span className="text-[11px] leading-[1.14] font-medium tabular-nums text-[color:var(--ink)]">
                                        {read.occurredAt ? fullFormatter.format(read.occurredAt) : "—"}
                                    </span>
                                    <span className="text-[11px] leading-[1.14] text-[color:var(--muted)]">
                                        {read.rpcName}
                                    </span>
                                </span>
                                <span className={`${NOTE_CLASS} text-[10px]`}>
                                    {read.sensitiveColumns.length
                                        ? read.sensitiveColumns.join(", ")
                                        : "No sensitive columns requested"}
                                </span>
                            </div>
                        ))}
                        {loading ? (
                            <p className={`${NOTE_CLASS} m-0 px-[9px] py-[7px] text-[10px]`}>
                                Loading more…
                            </p>
                        ) : null}
                    </>
                ) : (
                    <p className={`${NOTE_CLASS} m-0 px-[9px] py-[10px]`}>
                        {error
                            ? denialOrRawMessage(error, caps)
                            : loading
                                ? "Loading…"
                                : "No access recorded."}
                    </p>
                )}
            </div>
            {reads.length && error ? (
                <span className="text-[11px] leading-[1.35] text-red-400">
                    {denialOrRawMessage(error, caps)}
                </span>
            ) : null}
        </div>
    );
}

function AuditRow({ request, endpointNames, subjectNames, expanded, onToggle }) {
    const state = displayState(request);
    return (
        <article className="grid min-w-0 gap-[8px] border-b border-[color:var(--line-soft)] py-[12px] last:border-b-0">
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={expanded}
                className="grid gap-[6px] text-left cursor-pointer"
            >
                <span className="flex flex-wrap items-baseline justify-between gap-x-[12px] gap-y-[3px]">
                    <span className="flex flex-wrap items-baseline gap-x-[10px] gap-y-[3px]">
                        <StateChip state={state} />
                        <span className="text-[11px] leading-[1.14] text-[color:var(--muted)]">
                            {grantedWindow(request)} granted
                        </span>
                    </span>
                    <span className={`${NOTE_CLASS} text-[10px]`}>
                        {request.decidedAt ? dayFormatter.format(request.decidedAt) : ""}
                    </span>
                </span>
                <TargetChip
                    target={request.target}
                    endpointNames={endpointNames}
                    subjectNames={subjectNames}
                    showShape={false}
                />
                <span className={`${NOTE_CLASS} flex flex-wrap gap-x-[10px] gap-y-[2px] text-[11px]`}>
                    <span>
                        Requested by {request.requesterDisplay || "a former member"}
                    </span>
                    <span>·</span>
                    <span>
                        Approved by {request.decidedByDisplay || "a former member"}
                    </span>
                    {request.revokedAt ? (
                        <>
                            <span>·</span>
                            <span>Revoked {formatAgo(request.revokedAt)}</span>
                        </>
                    ) : null}
                </span>
                <span className="text-[11px] text-[color:var(--green)]">
                    {expanded ? "Hide access log" : "View access log"}
                </span>
            </button>
            {expanded ? <ReadTrail request={request} /> : null}
        </article>
    );
}

/**
 * Escalations that opened a window, and what each one read.
 *
 * A revocation ends the window without erasing it, so a revoked escalation
 * keeps its trail here: ending access early is the moment an approver is most
 * likely to come looking. Pending, denied and withdrawn requests never opened
 * one, so they have nothing to show.
 */
export default function EscalationAuditTab({ requests, endpointNames, subjectNames }) {
    const [openId, setOpenId] = useState(null);

    const granted = useMemo(() => requests.filter(openedWindow), [requests]);

    if (!granted.length) {
        return (
            <p className="m-0 py-[12px] text-[12px] leading-[1.45] text-[color:var(--muted)]">
                No approved requests.
            </p>
        );
    }

    return (
        <div className="grid gap-[8px] pb-[16px]">
            <p className={`${NOTE_CLASS} m-0 text-[11px] leading-[1.45]`}>
                Requests made using temporary access. Logs contain timestamps and field names;
                content is excluded.
            </p>
            <div className="grid border-y border-[color:var(--line)]">
                {granted.map((request) => (
                    <AuditRow
                        key={request.id}
                        request={request}
                        endpointNames={endpointNames}
                        subjectNames={subjectNames}
                        expanded={openId === request.id}
                        onToggle={() => setOpenId((current) => (
                            current === request.id ? null : request.id
                        ))}
                    />
                ))}
            </div>
        </div>
    );
}
